<script setup lang="ts">
import type { MessageSchema } from '@/i18n';
import { useDishesStore } from '@/stores/dishesStore';
import { useUserStore } from '@/stores/useUserStore';
import type { DishTag } from '@/types/api';
import Button from 'primevue/button';
import Chip from 'primevue/chip';
import Dialog from 'primevue/dialog';
import MultiSelect from 'primevue/multiselect';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref, watch, type ModelRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

// 1. Imports pour l'onboarding
import { useEventBus } from '@/composables/useEventBus';
import { useOnboarding } from '@/composables/useOnboarding';

const eventBus = useEventBus();
const { isOnboardingActive } = useOnboarding();

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const openAddTagToDishDialog: ModelRef<boolean | undefined> = defineModel('visible', {
  type: Boolean,
  required: true,
});
const dishesStore = useDishesStore();
const props = defineProps<{
  selectedTags: DishTag[] | [];
}>();

const toast = useToast();
const user = ref(useUserStore().user);
const restaurantId: string = user.value?.restaurants[0].id || '';
const newTagSelection: Ref<DishTag[] | []> = ref([]);

const emit = defineEmits<{
  (e: 'closeSidebar'): void;
  (e: 'updateSelection', newTagSelection: DishTag[]): void;
}>();

const isTagsLoaded = ref(false);

// Surveiller l'état d'onboarding et nettoyer les tags DB quand l'onboarding devient actif
watch(isOnboardingActive, (newValue) => {
  if (newValue) {
    console.log('[AddTagDialog] Onboarding activé - nettoyage des tags DB');
    dishesStore.initOnboardingMode();
  }
});

// Watcher pour synchroniser la sélection quand les tags du store changent
watch(
  () => dishesStore.tags,
  () => {
    // Re-synchroniser la sélection avec les nouvelles références d'objets
    if (newTagSelection.value.length > 0) {
      newTagSelection.value = newTagSelection.value.map((selectedTag) => {
        // Trouver l'objet correspondant dans le store mis à jour
        const updatedTag = dishesStore.tags.find((t) => t.id === selectedTag.id);
        return updatedTag || selectedTag;
      });
    }
  },
  { deep: true },
);

// 2. Variable pour savoir si on est en train de sauvegarder
const isSaving = ref(false);

watch(openAddTagToDishDialog, async (newValue) => {
  if (newValue) {
    isSaving.value = false; // IMPORTANT : On reset à l'ouverture
    isTagsLoaded.value = false;

    // En mode onboarding, toujours forcer le rechargement des tags locaux
    if (isOnboardingActive.value) {
      dishesStore.initOnboardingMode();
    } else if (dishesStore.tags.length === 0) {
      await dishesStore.fetchTags(restaurantId, toast, false);
    }

    // Remove pivot from tag and match with store objects
    newTagSelection.value = props.selectedTags.map((tag) => {
      const { pivot: _unused, ...rest } = tag;
      // Trouver l'objet correspondant dans le store pour avoir la bonne référence
      return dishesStore.tags.find((t) => t.id === rest.id) || rest;
    });
    isTagsLoaded.value = true;
  }
});

watch(
  () => props.selectedTags,
  (newTags) => {
    if (newTags) {
      // Remove pivot from tags and match with store objects
      newTagSelection.value = newTags.map((tag) => {
        const { pivot: _unused, ...rest } = tag;
        // Trouver l'objet correspondant dans le store pour avoir la bonne référence
        return dishesStore.tags.find((t) => t.id === rest.id) || rest;
      });
    }
  },
  { immediate: true },
);

async function updateTagsOnDish() {
  // 3. On indique qu'on sauvegarde (pour ne pas déclencher l'annulation)
  isSaving.value = true;

  emit('updateSelection', newTagSelection.value);
  openAddTagToDishDialog.value = false;
}

onMounted(async () => {
  if (!user.value) return;

  // Si l'onboarding est actif, forcer l'initialisation du mode onboarding
  if (isOnboardingActive.value) {
    console.log('[AddTagDialog] onMounted avec onboarding actif - initialisation du mode onboarding');
    dishesStore.initOnboardingMode();
    isTagsLoaded.value = true;
    return;
  }

  // Sinon, charger normalement si pas de tags
  if (dishesStore.tags.length === 0) {
    dishesStore.fetchTags(restaurantId, toast, false);
  }
  isTagsLoaded.value = true;
});

const formattedTags = computed(() => {
  return newTagSelection.value.map((tag) => {
    if (tag.parent_dish_tag_id && dishesStore.tags.length > 0) {
      // get parent dish tag from the dishesStore.tags
      const parentTag = dishesStore.tags.find((t) => t.id === tag.parent_dish_tag_id);
      if (parentTag) {
        return {
          ...tag,
          displayName: `${parentTag.name} ${tag.name === 'uncategorized' ? '' : ' | ' + tag.name}`,
        };
      }
    }
    return {
      ...tag,
      displayName: te(`dishTag.tagNames.${tag.name}`) ? t(`dishTag.tagNames.${tag.name}`) : tag.name,
    };
  });
});

async function removeTag(tag) {
  newTagSelection.value = newTagSelection.value.filter((t) => t.id !== tag.id);
}

function handleCancel() {
  if (isOnboardingActive.value) {
    eventBus.emit('onboarding:dialog-canceled-by-user-during-tags-selection');
  }
  openAddTagToDishDialog.value = false;
}

// 4. Fonction qui détecte TOUTES les fermetures (Croix, Masque, Annuler)
function onDialogVisibilityChange(isVisible: boolean) {
  // Si ça se ferme (false) ET que l'onboarding est actif ET qu'on n'a pas cliqué sur sauvegarder
  if (!isVisible && isOnboardingActive.value && !isSaving.value) {
    console.log('[AddTagDialog] Fermé par croix/annuler -> Retour arrière Onboarding');
    eventBus.emit('onboarding:dialog-canceled-by-user-during-tags-selection');
  }
}
</script>

<template>
  <Dialog
    v-model:visible="openAddTagToDishDialog"
    modal
    :header="t('dishTag.add.label')"
    :style="{ width: '25rem', position: 'relative', maxWidth: '90vw' }"
    :draggable="!isOnboardingActive"
    :pt="{ root: { class: 'onboarding-choose-folder-dialog' } }"
    @update:visible="onDialogVisibilityChange"
  >
    <div v-if="dishesStore.tags.length === 0">
      <p>{{ t('dishTag.noTagsAvailable') }}</p>
    </div>
    <div v-else class="mb-6 flex flex-col gap-4">
      <div v-if="newTagSelection.length === 0">
        <p>{{ t('dishTag.noTagSelected') }}</p>
      </div>
      <div v-else class="flex flex-row flex-wrap gap-2">
        <Chip v-for="tag in formattedTags" :key="tag.id" :label="tag.displayName" removable>
          <template #default>
            <div class="mx-0 my-1.5 text-xs leading-4" style="font-size: 12px">
              {{ tag.displayName }}
            </div>
          </template>
          <template #removeicon>
            <i class="pi pi-times-circle" style="cursor: pointer" @click="removeTag(tag)" />
          </template>
        </Chip>
      </div>
      <MultiSelect
        v-model="newTagSelection"
        :options="dishesStore.tags"
        :option-label="(option) => (te(`dishTag.tagNames.${option.name}`) ? t(`dishTag.tagNames.${option.name}`) : option.name)"
        :placeholder="t('dishTag.selectTags')"
        :max-selected-labels="0"
        :selected-items-label="t('dishTag.itemsSelected', { count: newTagSelection.length })"
        class="md:w-20rem w-full"
        option-group-label="name"
        option-group-children="child_tags"
        :pt="{ panel: { style: { 'z-index': '99999 !important' } } }"
      />
    </div>
    <div class="flex justify-between gap-2">
      <Button type="button" :label="t('common.cancel')" outlined :disabled="false" @click="handleCancel"></Button>
      <div class="flex justify-end gap-2">
        <Button type="button" :label="t('common.save')" :disabled="false" class="onboarding-add-tag" @click="updateTagsOnDish"></Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
/* Force z-index for multiselect panel to appear above Shepherd dialogs */
/* :global(.p-multiselect-panel) {
  z-index: 10001 !important;
}

:global(.p-multiselect-overlay) {
  z-index: 10001 !important;
}

:global([data-pc-section='panel']) {
  z-index: 10001 !important;
}

*/

/* Assure que le dropdown MultiSelect soit au-dessus de tout */
/* :global(.mobile-scrollable-dropdown) {
  z-index: 10001 !important;
}

*/
</style>
